/**
 * @file SkeletonLoader.tsx
 * @description Loading skeleton placeholders.
 * MUST be used on every page and data card during loading states.
 * NEVER show a blank screen or spinner alone.
 *
 * @example
 * // Card placeholder
 * <SkeletonLoader variant="card" />
 *
 * // Table rows
 * <SkeletonLoader variant="table-row" count={5} />
 *
 * // Simple height-based skeleton (used by dashboard cards)
 * <SkeletonLoader height={180} />
 */

export type SkeletonVariant = 'card' | 'text' | 'avatar' | 'table-row' | 'donut';

interface SkeletonLoaderProps {
  /** The type of placeholder to render */
  variant?: SkeletonVariant;
  /** Number of repetitions (for text lines, table rows). Default: 1 */
  count?: number;
  /** Optional extra class or style overrides */
  className?: string;
  /** Simple height-based skeleton (alternative to variant) */
  height?: number;
}

/** Shared shimmer animation style */
const shimmerStyle: React.CSSProperties = {
  background: 'linear-gradient(90deg, var(--surface-2) 25%, var(--border) 50%, var(--surface-2) 75%)',
  backgroundSize: '400px 100%',
  animation: 'shimmer 1.4s ease infinite',
  borderRadius: 8,
};

/** A single shimmer block with custom dimensions. */
function ShimmerBlock({
  width = '100%',
  height = 16,
  borderRadius = 8,
  style,
}: {
  width?: string | number;
  height?: number;
  borderRadius?: number;
  style?: React.CSSProperties;
}) {
  return (
    <div
      aria-hidden="true"
      style={{
        ...shimmerStyle,
        width,
        height,
        borderRadius,
        flexShrink: 0,
        ...style,
      }}
    />
  );
}

/** Card skeleton: icon + title + two text lines + sparkline bar. */
function CardSkeleton() {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1.5px solid var(--border)',
        borderRadius: 18,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <ShimmerBlock width={40} height={40} borderRadius={10} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ShimmerBlock width="60%" height={14} />
          <ShimmerBlock width="35%" height={10} />
        </div>
      </div>
      <ShimmerBlock height={28} />
      <ShimmerBlock width="45%" height={10} />
    </div>
  );
}

/** Text skeleton: stacked shimmer lines. */
function TextSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <ShimmerBlock height={14} />
      <ShimmerBlock width="90%" height={14} />
      <ShimmerBlock width="75%" height={14} />
    </div>
  );
}

/** Avatar skeleton: circle + two text lines beside it. */
function AvatarSkeleton() {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
      <ShimmerBlock width={40} height={40} borderRadius={9999} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <ShimmerBlock width="50%" height={13} />
        <ShimmerBlock width="35%" height={10} />
      </div>
    </div>
  );
}

/** Table-row skeleton: a row of shimmering cells. */
function TableRowSkeleton() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr 1fr',
        gap: 16,
        padding: '12px 16px',
        borderBottom: '1px solid var(--border)',
        alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <ShimmerBlock width={32} height={32} borderRadius={8} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <ShimmerBlock width="70%" height={12} />
          <ShimmerBlock width="50%" height={9} />
        </div>
      </div>
      <ShimmerBlock height={12} />
      <ShimmerBlock height={22} borderRadius={20} />
      <ShimmerBlock height={12} />
    </div>
  );
}

/** Donut chart skeleton: circle + legend items. */
function DonutSkeleton() {
  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'center', padding: 20 }}>
      {/* Donut ring */}
      <div
        aria-hidden="true"
        style={{
          width: 100,
          height: 100,
          borderRadius: '50%',
          border: '14px solid var(--border)',
          background: 'transparent',
          flexShrink: 0,
          animation: 'pulse 1.4s ease infinite',
        }}
      />
      {/* Legend */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <ShimmerBlock width={10} height={10} borderRadius={2} />
            <ShimmerBlock width={`${65 - i * 10}%`} height={11} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * SkeletonLoader
 * Renders the appropriate placeholder for the given `variant`.
 * Pass `count` to render multiple repetitions.
 * Alternatively, pass just `height` for a simple shimmer block.
 */
export function SkeletonLoader({
  variant,
  count = 1,
  className,
  height,
}: SkeletonLoaderProps) {
  /** Simple height-based mode — used by dashboard cards */
  if (height && !variant) {
    return (
      <div
        className={className}
        role="status"
        aria-label="Loading…"
        aria-live="polite"
        style={{ width: '100%' }}
      >
        <ShimmerBlock height={height} borderRadius={18} />
      </div>
    );
  }

  const items = Array.from({ length: count });

  return (
    <div
      className={className}
      role="status"
      aria-label="Loading…"
      aria-live="polite"
      style={{ width: '100%' }}
    >
      {items.map((_, i) => {
        switch (variant) {
          case 'card':
            return <CardSkeleton key={i} />;
          case 'text':
            return (
              <div key={i} style={{ marginBottom: i < items.length - 1 ? 16 : 0 }}>
                <TextSkeleton />
              </div>
            );
          case 'avatar':
            return (
              <div key={i} style={{ marginBottom: i < items.length - 1 ? 12 : 0 }}>
                <AvatarSkeleton />
              </div>
            );
          case 'table-row':
            return <TableRowSkeleton key={i} />;
          case 'donut':
            return <DonutSkeleton key={i} />;
          default:
            return <CardSkeleton key={i} />;
        }
      })}
    </div>
  );
}

/** Default export for backward compatibility */
export default SkeletonLoader;